"use client"

import { useCallback, useState } from "react"

import { ChevronLeft, X } from "lucide-react"

import { Button } from "@/components/ui/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog"

import {
  AssignmentFormData,
  DEFAULT_ASSIGNMENT_VALUES,
  INITIAL_FORM_VALUES,
} from "./types"

import { AssignmentWizardController } from "./AssignmentWizard.controller"
import { StepIndicator } from "./StepIndicator"
import { PracticeTopicStep } from "./steps/PracticeTopicStep"
import { GradeStep } from "./steps/GradeStep"
import { ClassTimeStep } from "./steps/ClasstimeStep"
import { CurriculumStep } from "./steps/CurriculumStep"
import { PracticeTypeStep } from "./steps/PracticetypeStep"
import { AdditionalConsiderationsStep } from "./steps/AdditionalConsiderationsStep"
import { ReviewStep } from "./steps/ReviewStep"
import { SummaryStep } from "./steps/SummaryStep"

interface AssignmentWizardProps {
  controller: AssignmentWizardController
}

const TOTAL_STEPS = 8

const STEP_FIELD_MAP: Record<number, keyof AssignmentFormData> = {
  1: "practiceTopic",
  2: "grade",
  3: "classTime",
  4: "curriculum",
  5: "practiceType",
  6: "additionalConsiderations",
}

export function AssignmentWizard({ controller }: AssignmentWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] =
    useState<AssignmentFormData>(INITIAL_FORM_VALUES)
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>(
    {},
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [summary, setSummary] = useState("")
  const [isLoadingSummary, setIsLoadingSummary] = useState(false)

  // Create individual memoized validation handlers for each step
  const handlePracticeTopicStepValidation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, 1: isValid }))
  }, [])

  const handleGradeStepValidation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, 2: isValid }))
  }, [])

  const handleClassTimeStepValidation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, 3: isValid }))
  }, [])

  const handleCurriculumStepValidation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, 4: isValid }))
  }, [])

  const handlePracticeTypeStepValidation = useCallback((isValid: boolean) => {
    setStepValidation((prev) => ({ ...prev, 5: isValid }))
  }, [])

  const handleAdditionalConsiderationsStepValidation = useCallback(
    (isValid: boolean) => {
      setStepValidation((prev) => ({ ...prev, 6: isValid }))
    },
    [],
  )

  function isFieldEmpty(field: keyof AssignmentFormData): boolean {
    const value = formData[field]
    return value === "" || (Array.isArray(value) && value.length === 0)
  }

  function fillEmptyField(field: keyof AssignmentFormData) {
    if (isFieldEmpty(field)) {
      setFormData((prev) => ({
        ...prev,
        [field]: DEFAULT_ASSIGNMENT_VALUES[field],
      }))
    }
  }

  function fillAllEmptyFields() {
    setFormData((prev) => ({
      practiceTopic:
        prev.practiceTopic || DEFAULT_ASSIGNMENT_VALUES.practiceTopic,
      grade: prev.grade || DEFAULT_ASSIGNMENT_VALUES.grade,
      classTime: prev.classTime || DEFAULT_ASSIGNMENT_VALUES.classTime,
      curriculum:
        prev.curriculum.length > 0
          ? prev.curriculum
          : DEFAULT_ASSIGNMENT_VALUES.curriculum,
      practiceType: prev.practiceType || DEFAULT_ASSIGNMENT_VALUES.practiceType,
      additionalConsiderations:
        prev.additionalConsiderations ||
        DEFAULT_ASSIGNMENT_VALUES.additionalConsiderations,
    }))
  }

  function goToNextStep() {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1)
    }
  }

  async function handleNext() {
    // If we're at the review step (7), generate summary and move to summary step (8)
    if (currentStep === TOTAL_STEPS - 1) {
      fillAllEmptyFields()
      await generateAndShowSummary()
    } else {
      goToNextStep()
    }
  }

  async function generateAndShowSummary() {
    setIsLoadingSummary(true)
    setSummary("")
    goToNextStep() // Move to summary step

    try {
      const summaryText = await controller.generateSummary(formData)
      setSummary(summaryText)
    } catch (error) {
      setErrorMessage(
        "Failed to generate summary. Please try again or contact us at support@classkick.com if the issue persists.",
      )
      setCurrentStep(7) // Go back to review
    } finally {
      setIsLoadingSummary(false)
    }
  }

  async function handleRefineRequest(userMessage: string): Promise<string> {
    try {
      const refinedSummary = await controller.refineAssignment(userMessage)
      setSummary(refinedSummary)
      return refinedSummary
    } catch (error) {
      throw new Error(
        "Failed to refine assignment. Please try again or contact us at support@classkick.com if the issue persists.",
      )
    }
  }

  function handleSkip() {
    const currentField = STEP_FIELD_MAP[currentStep]
    if (currentField) {
      fillEmptyField(currentField)
    }

    if (currentStep === TOTAL_STEPS - 1) {
      fillAllEmptyFields()
    }

    goToNextStep()
  }

  function handleSkipAll() {
    fillAllEmptyFields()
    setCurrentStep(TOTAL_STEPS - 1) // Go to review, not summary
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  async function handleSubmit() {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      await controller.createAssignment(formData)
      controller.viewAssignment()
    } catch (error) {
      setErrorMessage(
        "Failed to create assignment. Please try again or contact us at support@classkick.com if the issue persists.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleStepClick(step: number) {
    // Don't allow clicking on summary step directly
    if (step === TOTAL_STEPS) return

    // If going back from summary step, reset the cache
    if (currentStep === TOTAL_STEPS) {
      controller.resetSummaryGeneratedCache()
      setSummary("")
    }

    // Fill empty fields for steps being skipped over
    if (step > currentStep) {
      for (let i = currentStep; i < step; i++) {
        const field = STEP_FIELD_MAP[i]
        if (field) {
          fillEmptyField(field)
        }
      }
      if (step === TOTAL_STEPS - 1) {
        fillAllEmptyFields()
      }
    }
    setCurrentStep(step)
  }

  function renderStep() {
    switch (currentStep) {
      case 1:
        return (
          <PracticeTopicStep
            value={formData.practiceTopic}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, practiceTopic: value }))
            }
            onValidation={handlePracticeTopicStepValidation}
          />
        )
      case 2:
        return (
          <GradeStep
            value={formData.grade}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, grade: value }))
            }
            onValidation={handleGradeStepValidation}
          />
        )
      case 3:
        return (
          <ClassTimeStep
            value={formData.classTime}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, classTime: value }))
            }
            onValidation={handleClassTimeStepValidation}
          />
        )
      case 4:
        return (
          <CurriculumStep
            value={formData.curriculum}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, curriculum: value }))
            }
            onValidation={handleCurriculumStepValidation}
          />
        )
      case 5:
        return (
          <PracticeTypeStep
            value={formData.practiceType}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, practiceType: value }))
            }
            onValidation={handlePracticeTypeStepValidation}
          />
        )
      case 6:
        return (
          <AdditionalConsiderationsStep
            value={formData.additionalConsiderations}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                additionalConsiderations: value,
              }))
            }
            onValidation={handleAdditionalConsiderationsStepValidation}
          />
        )
      case 7:
        return <ReviewStep formData={formData} onEdit={setCurrentStep} />
      case 8:
        return (
          <SummaryStep
            initialSummary={summary}
            isInitialLoading={isLoadingSummary}
            isSubmitting={isSubmitting}
            onConfirm={handleSubmit}
            onBack={() => {
              controller.resetSummaryGeneratedCache()
              setSummary("")
              setCurrentStep(7)
            }}
            onRefineRequest={handleRefineRequest}
          />
        )
      default:
        return null
    }
  }

  const isReviewStep = currentStep === TOTAL_STEPS - 1
  const isSummaryStep = currentStep === TOTAL_STEPS
  const canGoBack = currentStep > 1 && !isSummaryStep
  const isCurrentStepValid = stepValidation[currentStep] !== false

  return (
    <>
      {/* Error Dialog */}
      <Dialog
        open={!!errorMessage}
        onOpenChange={(open) => !open && setErrorMessage(null)}
      >
        <DialogContent className="tw-max-w-xl">
          <DialogHeader>
            <DialogTitle className="tw-text-center tw-text-2xl tw-font-black tw-text-red-600">
              Oops! Something went wrong
            </DialogTitle>
            <DialogDescription className="tw-text-center tw-py-6 tw-text-gray-700">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="tw-flex tw-justify-center tw-gap-3">
            <DialogClose asChild>
              <Button variant="outline" size="lg">
                Close
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-green-50 tw-to-blue-50">
        {/* Header */}
        <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-px-6 tw-py-4 tw-relative tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3 tw-z-10">
            {canGoBack && (
              <Button
                onClick={handleBack}
                className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors tw-p-2 tw-rounded-lg hover:tw-bg-gray-100"
                aria-label="Go back"
                variant="ghost"
              >
                <ChevronLeft className="tw-w-10 tw-h-10" />
              </Button>
            )}
          </div>
          <h2 className="tw-absolute tw-left-0 tw-right-0 tw-text-center tw-text-xl tw-font-semibold tw-text-gray-700 tw-pointer-events-none">
            Create Assignment
          </h2>
          <div className="tw-z-10">
            <Button
              onClick={() => controller.closeWizard()}
              className="tw-text-gray-600 hover:tw-text-gray-800 tw-transition-colors tw-rounded-xl"
              variant="ghost"
            >
              <X className="tw-w-6 tw-h-6" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex tw-flex-col tw-h-full">
          <div
            className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-3 tw-overflow-auto tw-flex-1"
            style={{ height: `${window.innerHeight / 1.9}px` }}
          >
            <div className="tw-w-full tw-max-w-3xl">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                className="tw-space-y-12 tw-pt-4"
              >
                {renderStep()}

                {/* Primary Button - Only show when not summary step */}
                {!isSummaryStep && (
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-8 tw-pt-18">
                    <div className="tw-flex tw-justify-center">
                      <Button
                        type="button"
                        onClick={handleNext}
                        size="lg"
                        className="tw-min-w-[120px] tw-bg-emerald-600 hover:tw-bg-emerald-800 tw-rounded-full tw-text-sm"
                        disabled={!isCurrentStepValid}
                      >
                        {isReviewStep ? "LOOKS GOOD" : "NEXT"}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Fixed Bottom Navigation */}
          {!isSummaryStep && (
            <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-border-t tw-border-gray-00 tw-py-4">
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                {/* Secondary Navigation Links */}
                {!isReviewStep && (
                  <div className="tw-flex tw-items-center tw-gap-6">
                    {canGoBack && (
                      <Button
                        type="button"
                        variant="link"
                        onClick={handleBack}
                        className="tw-text-md tw-text-gray-600 hover:tw-text-gray-700"
                      >
                        Back &lt;
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="link"
                      onClick={handleSkip}
                      className="tw-text-md tw-text-gray-600 hover:tw-text-gray-700"
                    >
                      Skip &gt;
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      onClick={handleSkipAll}
                      className="tw-text-md tw-text-gray-600 hover:tw-text-gray-700"
                    >
                      Skip to final step ≫
                    </Button>
                  </div>
                )}

                {/* Step Indicator */}
                <StepIndicator
                  currentStep={currentStep}
                  totalSteps={7}
                  onStepClick={handleStepClick}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
